import React, { useState } from "react";
import { Trash2, Upload, FileText, Eye } from "react-feather";
import toast from "react-hot-toast";
import AttachmentPreviewModal from "../../../components/common/AttachmentPreviewModal";
import InlineFilePreview from "./InlineFilePreview";
import {
  useUploadVoucherReceiptMutation,
  useDeleteVoucherReceiptMutation,
  useGetVoucherReceiptContentQuery,
} from "../pettyCashApi";

// Single-file variant of the upload/view(blob)/delete contract established
// by EmployeeDocuments.jsx - a Voucher has exactly one receipt (2MB cap,
// same as the API's own RequestSizeLimit), not a document list.
//
// Since 2026-09-30 the receipt is shown inline (picture, or the PDF's first page)
// as soon as the voucher opens - "still need to click on view icon" was the
// complaint. Clicking the preview opens the full-size modal.
const ReceiptUpload = ({ recordGuid, hasReceipt, fileName, canManage }) => {
  const [uploadReceipt, { isLoading: isUploading }] = useUploadVoucherReceiptMutation();
  const [deleteReceipt] = useDeleteVoucherReceiptMutation();
  const { data: blob, isFetching, isError } = useGetVoucherReceiptContentQuery(recordGuid, { skip: !hasReceipt });
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleUpload = async () => {
    if (!selectedFile) return toast.error("Choose a file first");
    if (selectedFile.size > 2 * 1024 * 1024) return toast.error("File must be 2MB or smaller");
    try {
      await uploadReceipt({ recordGuid, file: selectedFile }).unwrap();
      toast.success("Receipt uploaded");
      setSelectedFile(null);
    } catch (error) {
      toast.error(error?.message || "Could not upload receipt");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this receipt?")) return;
    try {
      await deleteReceipt(recordGuid).unwrap();
      toast.success("Receipt removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove receipt");
    }
  };

  return (
    <div>
      <label className="form-label">Receipt</label>
      {hasReceipt ? (
        <div className="d-flex align-items-start gap-3 flex-wrap">
          <InlineFilePreview
            blob={blob}
            fileName={fileName}
            loading={isFetching || !isError ? undefined : false}
            onOpen={blob ? () => setShowPreview(true) : undefined}
          />
          <div className="d-flex align-items-center gap-2">
            <FileText size={16} className="text-muted" />
            <span>{fileName || "Receipt"}</span>
            <button type="button" className="btn btn-link p-1" onClick={() => setShowPreview(true)} disabled={!blob} title="Open full size">
              <Eye size={16} />
            </button>
            {canManage && (
              <button type="button" className="btn btn-link p-1 text-danger" onClick={handleDelete} title="Remove">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        </div>
      ) : (
        canManage && (
          <div className="d-flex gap-2">
            <input
              type="file"
              className="form-control"
              style={{ maxWidth: 280 }}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
            <button type="button" className="btn btn-outline-primary" onClick={handleUpload} disabled={isUploading || !selectedFile}>
              <Upload size={14} className="me-1" /> {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        )
      )}
      {!hasReceipt && !canManage && <div className="text-muted small">No receipt attached.</div>}

      <AttachmentPreviewModal
        show={showPreview}
        onClose={() => setShowPreview(false)}
        blob={blob}
        fileName={fileName}
        title="Receipt"
      />
    </div>
  );
};

export default ReceiptUpload;
